<template>
  <SplitViewTemplate
    v-if="featureAreasLoaded"
    :screenConfig="data"
    :backButtonRoute="'/settings/system'"
    :dataModel="formModel"
    :formConfig="setFormConfig"
    :setLoading="setLoading"
    :validationSummary="validationSummary"
  />
</template>
<script>
import SplitViewTemplate from "../../../templates/split-view";
import RouteMixin from "@/_mixins/route.mixin";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import { FeatureStorage } from "@/_services/storage";
import { SplitViewService } from "../../../_services/api/templates/split-view.services";

export default {
  mixins: [RouteMixin],
  components: {
    SplitViewTemplate,
  },
  data() {
    return {
      setLoading: false,
      // gc-field-tag.vue reads dataSourceConfig.data once, in its own data() (not reactively),
      // so the distinct Feature Area values must be in place before it first mounts - gate the
      // whole screen on this rather than populating dataSourceConfig.data after the fact.
      featureAreasLoaded: false,
      validationSummary: [],
      formModel: {
        ApplicationFeatureCode: "",
        ScopeCode: "",
        ScopeValue: "",
        ScopeCodeDisplay: "",
        ScopeValueDisplay: "",
        SettingName: "",
        SettingValue: "",
        SystemDefaultValue: "",
        Description: "",
      },
      data: {
        title: "Application Feature Settings",
        pageRoute: "/application-feature-settings",
        recordEndPoint: "ApplicationFeatureSetting",
        updateEndPoint: "ApplicationFeatureSetting",
        updateKeyFields: [
          "ApplicationFeatureCode",
          "ScopeCode",
          "ScopeValue",
          "SettingName",
        ],
        exportFileName: "Application Feature Settings",
        recordPostProcess: this.recordPostProcess,
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "ApplicationFeatureSetting",
              // Bug #77958: a raw OData filter string here is silently dropped once
              // combined with the filter panel's own conditions (gc-data-grid.vue's
              // finalFilterValues only iterates non-string gridDefaultFilter arrays) -
              // must be DevExtreme's own structured filter expression, matching the
              // sibling Factors screen (system-parameters.vue).
              filter: [["Inactive", "=", "N"]],
              select: [
                "ApplicationFeatureCode",
                "ScopeCode",
                "ScopeValue",
                "SettingName",
                "SettingValue",
                "SystemDefaultValue",
                "Description",
              ],
              sort: [
                { selector: "ApplicationFeatureCode", desc: false },
                { selector: "SettingName", desc: false },
              ],
              key: [
                "ApplicationFeatureCode",
                "ScopeCode",
                "ScopeValue",
                "SettingName",
              ],
              serverPaging: true,
              allowGrouping: false,
            },
            rowSelect: {
              view: this,
              page: "application-feature-settings",
              mode: "review",
              idProperties: [
                "ApplicationFeatureCode",
                "ScopeCode",
                "ScopeValue",
                "SettingName",
              ],
            },
          },
          toolbar: {
            search: false,
            columnChooser: false,
            export: false,
            openFilter: true,
            expand: false,
            openFilterByDefault: true,
          },
          columns: [
            {
              screenField: "ApplicationFeatureCode",
              dataField: "ApplicationFeatureCode",
              label: "Feature Area",
              alignment: "left",
              sortIndex: 0,
              sortOrder: "asc",
              width: ColumnWidth.FeatureArea.width,
              minWidth: ColumnWidth.FeatureArea.minWidth,
            },
            {
              dataField: "SettingName",
              label: "Setting Name",
              sortIndex: 1,
              sortOrder: "asc",
              allowHiding: false,
            },
            {
              dataField: "SettingValue",
              label: "Setting Value",
              width: ColumnWidth.SettingValue.width,
              minWidth: ColumnWidth.SettingValue.minWidth,
              allowSearch: false,
            },
            {
              screenField: "ApplicationFeatureCode",
              dataField: "ApplicationFeatureCode",
              label: "Feature Area",
              type: "String",
              filterIndex: 0,
              visible: false,
              showInColumnChooser: false,
              allowHiding: false,
              dataSourceConfig: {
                data: [],
              },
            },
            {
              screenField: "ApplicationFeatureSettingParameter",
              filterLabel: "Setting",
              dataField: "SearchText",
              type: "Text",
              filterIndex: 1,
              visible: false,
              showInColumnChooser: false,
              allowHiding: false,
            },
          ],
        },
      },
    };
  },
  computed: {
    setFormConfig() {
      return {
        headerToolbar: {
          closeSplit: true,
          // Per-action gated (unlike Factors' hardcoded save:true) - a Read-only
          // user must not see the Edit icon, per BR/AC and TC-019.
          save: FeatureStorage.checkPermission(
            this.$route.meta.applicationFeatureCode,
            "create",
          ),
        },
        formItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 2,
            items: [
              {
                dataField: "ApplicationFeatureCode",
                editorType: "dxTextBox",
                label: { text: "Feature Area" },
                allowUpdate: false,
              },
              {
                dataField: "SettingName",
                editorType: "dxTextBox",
                label: { text: "Setting Name" },
                allowUpdate: false,
              },
              {
                dataField: "ScopeCodeDisplay",
                editorType: "dxTextBox",
                label: { text: "Scope Code" },
                allowUpdate: false,
              },
              {
                dataField: "ScopeValueDisplay",
                editorType: "dxTextBox",
                label: { text: "Scope Value" },
                allowUpdate: false,
              },
            ],
          },
          {
            itemType: "group",
            caption: "",
            colCount: 1,
            items: [
              {
                dataField: "SettingValue",
                editorType: "dxTextBox",
                label: { text: "Setting Value" },
                required: true,
                allowUpdate: this.baseView.mode !== "review",
                cssClass: "afs-required-field-item",
                editorOptions: {
                  maxLength: 1000,
                },
              },
              {
                dataField: "SystemDefaultValue",
                editorType: "dxTextBox",
                label: { text: "System Default Value" },
                allowUpdate: false,
              },
              {
                dataField: "Description",
                editorType: "dxTextArea",
                label: { text: "Description" },
                allowUpdate: false,
                cssClass: "afs-description-item",
              },
            ],
          },
        ],
        overWritePayload: this.overWritePayload,
      };
    },
  },
  async created() {
    // Distinct Feature Area values for the tag-box filter - ApplicationFeatureCode isn't its own
    // master table, so (unlike Branch/Division-style tag-box filters elsewhere) there's no
    // existing lookup endpoint to point the filter at; fetch the distinct values directly instead.
    // $filter and $apply can't be passed as separate query options together - the filter must
    // be nested inside the $apply expression itself. $select is dropped too since groupby
    // already projects only the grouped field.
    await SplitViewService.getItems({
      url: "api/" + this.data.recordEndPoint,
      apply: "filter(Inactive eq 'N')/groupby((ApplicationFeatureCode))",
    })
      .then((res) => {
        // $apply/groupby responses come back as a plain array, unlike a normal OData query's
        // {"@odata.context":..., "value":[...]} envelope - there is no .value to unwrap here.
        let filterColumn = this.data.gridData.columns.find(
          (c) => c.filterIndex === 0,
        );
        filterColumn.dataSourceConfig.data = res.data.map(
          (item) => item.ApplicationFeatureCode,
        );
      })
      .catch((err) => {
        this.$logger.debug("Feature Area list load err ", err);
      })
      .finally(() => {
        this.featureAreasLoaded = true;
      });
  },
  methods: {
    // Blank Scope Code/Scope Value display as "—" (§6/TC-008) via separate display-only
    // fields, so the real ScopeCode/ScopeValue used to build the composite PATCH key
    // (updateKeyFields) is never touched by the display transform.
    recordPostProcess(data) {
      data["ScopeCodeDisplay"] = data["ScopeCode"] || "—";
      data["ScopeValueDisplay"] = data["ScopeValue"] || "—";
      return data;
    },
    overWritePayload(payload) {
      delete payload.ScopeCodeDisplay;
      delete payload.ScopeValueDisplay;
      return payload;
    },
  },
};
</script>

<style scoped>
::v-deep .afs-required-field-item {
  margin-bottom: 10px;
}

::v-deep .afs-description-item {
  margin-top: 5px;
}

::v-deep .afs-description-item .dx-field-item-label-content {
  margin-top: 10px;
}
</style>
